import { useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GameFrame, BetControls } from "@/components/GameFrame";
import { useAudio } from "@/context/GameFeel";

const Card = ({ c }) => (
  <div className={`w-14 h-20 rounded-lg flex items-center justify-center text-lg font-bold ${c.r === "?" ? "bg-gradient-to-br from-amber-500 to-purple-600 text-white" : "bg-white " + (c.s === "♥" || c.s === "♦" ? "text-red-600" : "text-black")}`}>
    {c.r === "?" ? "?" : `${c.r}${c.s}`}
  </div>
);

export default function BlackjackGame() {
  const { user, setUser } = useAuth();
  const { react, sfx } = useAudio();
  const [bet, setBet] = useState(100);
  const [round, setRound] = useState(null);
  const [busy, setBusy] = useState(false);

  const active = round && round.status === "active";

  const deal = async () => {
    if (bet > (user?.coins ?? 0)) return toast.error("Not enough coins");
    setBusy(true);
    try {
      const { data } = await api.post("/tables/blackjack/deal", { bet });
      setRound(data); setUser((u) => ({ ...u, coins: data.coins }));
      if (data.status === "done") settleToast(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const act = async (kind) => {
    try {
      const { data } = await api.post(`/tables/blackjack/${kind}`, { round_id: round.round_id });
      sfx("tap");
      setRound(data);
      if (data.status === "done") { setUser((u) => ({ ...u, coins: data.coins })); settleToast(data); }
    } catch (e) { toast.error(apiErr(e)); }
  };
  const settleToast = (d) => {
    const map = { blackjack: "Blackjack! 🎉", win: "You win!", push: "Push", bust: "Bust!", lose: "Dealer wins" };
    const won = d.payout > d.bet || d.result === "blackjack" || d.result === "win";
    won ? toast.success(`${map[d.result]} +${d.payout.toLocaleString()}`) : toast(map[d.result] || d.result);
    react({ outcome: won ? "win" : d.result === "push" ? "draw" : "loss", game: "card_21", big: d.result === "blackjack" });
  };

  return (
    <GameFrame title="Blackjack" subtitle="Beat the dealer · coins only"
      side={<>
        {active ? (
          <div className="glass rounded-2xl p-5 border border-white/10 flex gap-3">
            <button data-testid="blackjack-hit" onClick={() => act("hit")} className="flex-1 py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-purple-600 font-bold">HIT</button>
            <button data-testid="blackjack-stand" onClick={() => act("stand")} className="flex-1 py-3.5 rounded-xl bg-white/10 border border-white/10 font-bold">STAND</button>
          </div>
        ) : (
          <BetControls bet={bet} setBet={setBet} coins={user?.coins} disabled={busy} onPlay={deal} playLabel={busy ? "Dealing…" : "DEAL"} testid="blackjack-deal" />
        )}
      </>}>
      <div className="glass rounded-3xl p-8 border border-amber-500/20 glow-coins">
        <div className="mb-6">
          <p className="text-sm text-gray-400 mb-2">Dealer {round && round.status === "done" ? `· ${round.dealer_total}` : ""}</p>
          <div className="flex gap-2 min-h-[80px]">{(round?.dealer || [{ r: "?", s: "" }, { r: "?", s: "" }]).map((c, i) => <Card key={i} c={c} />)}</div>
        </div>
        <div>
          <p className="text-sm text-gray-400 mb-2">You {round ? `· ${round.player_total}` : ""}</p>
          <div className="flex gap-2 flex-wrap min-h-[80px]">{(round?.player || [{ r: "?", s: "" }, { r: "?", s: "" }]).map((c, i) => <Card key={i} c={c} />)}</div>
        </div>
        {round?.status === "done" && <p className="text-center mt-6 px-6 py-3 rounded-xl bg-white/5 text-gray-300 capitalize">{round.result} · {round.payout > 0 ? `+${round.payout.toLocaleString()} coins` : "no win"}</p>}
      </div>
    </GameFrame>
  );
}
